import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import i18n from "@/i18n";
import {
  DEFAULT_LOCALE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  LocaleDefinition,
  getLocaleDefinition,
  normalizeLanguage,
} from "@/config/i18n";

type TranslateOptions = Record<string, unknown> & { defaultValue?: string };

interface LanguageContextValue {
  /** Code court courant : "fr", "en", ... */
  language: string;
  /** Locale complète courante : "fr-FR", "en-US", ... */
  locale: string;
  /** Définition complète (label, drapeau, direction) */
  localeDefinition: LocaleDefinition;
  /** Liste des langues supportées */
  languages: LocaleDefinition[];
  /** Traduit une clé, avec repli sur la valeur par défaut fournie */
  t: (key: string, options?: TranslateOptions) => string;
  /** Change la langue instantanément (sans rechargement) et la persiste */
  setLanguage: (code: string) => void;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<string>(() =>
    normalizeLanguage(
      (typeof window !== "undefined" && localStorage.getItem(LANGUAGE_STORAGE_KEY)) ||
        i18n.language ||
        DEFAULT_LOCALE,
    ),
  );

  // Reste synchronisé avec i18next (détection géo, réglages utilisateur, etc.)
  useEffect(() => {
    const onChange = (lng: string) => setLanguageState(normalizeLanguage(lng));
    i18n.on("languageChanged", onChange);
    if (normalizeLanguage(i18n.language) !== language) {
      i18n.changeLanguage(language);
    }
    return () => {
      i18n.off("languageChanged", onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const localeDefinition = useMemo(() => getLocaleDefinition(language), [language]);

  // Applique lang/dir sur <html> pour l'accessibilité et le RTL
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = language;
    document.documentElement.dir = localeDefinition.dir;
  }, [language, localeDefinition.dir]);

  const setLanguage = useCallback((code: string) => {
    const next = normalizeLanguage(code);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    i18n.changeLanguage(next);
    setLanguageState(next);
  }, []);

  const t = useCallback(
    (key: string, options?: TranslateOptions) => {
      const value = i18n.t(key, options as never);
      // i18next renvoie la clé si aucune traduction n'existe
      if (typeof value === "string" && value === key && options?.defaultValue) {
        return options.defaultValue;
      }
      return typeof value === "string" ? value : key;
    },
    // `language` en dépendance : force la ré-évaluation au changement de langue
    [language],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      locale: localeDefinition.locale,
      localeDefinition,
      languages: SUPPORTED_LOCALES,
      t,
      setLanguage,
      isRTL: localeDefinition.dir === "rtl",
    }),
    [language, localeDefinition, t, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage doit être utilisé à l'intérieur d'un <LanguageProvider>");
  }
  return ctx;
};
